import React from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import styles from "../../../styles/styles";
import { backend_url } from "../../../server";
import { withAdvertiseEventFormAnchor } from "../../../utils/advertiseEventLink";

const buildImageUrl = (image) => {
  if (!image) return "";

  if (image.startsWith("http://") || image.startsWith("https://")) {
    return image;
  }

  return `${backend_url}${image}`;
};

const resolveHeroButtonAlign = (slide) => {
  const align = String(slide?.buttonAlign || "").toLowerCase();
  if (align === "center" || align === "right" || align === "left") return align;
  const x = Number(slide?.buttonPositionX);
  if (Number.isFinite(x)) {
    if (x < 34) return "left";
    if (x < 67) return "center";
    return "right";
  }
  return "left";
};

const buttonJustifyClass = (align) =>
  align === "center" ? "justify-center" : align === "right" ? "justify-end" : "justify-start";

const HeroSlideButton = ({ slide, className = "" }) => {
  const label = String(slide?.buttonText || "").trim();
  const href = String(slide?.buttonLink || "").trim();
  if (!label) return null;

  const buttonEl = (
    <div className={`${styles.button} ${className}`.trim()}>
      <span className="text-[#fff] font-[Poppins] text-[18px]">{label}</span>
    </div>
  );

  if (!href) {
    return <div className="inline-block">{buttonEl}</div>;
  }

  const isExternal = href.startsWith("http://") || href.startsWith("https://");
  if (isExternal) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className="inline-block">
        {buttonEl}
      </a>
    );
  }

  return (
    <Link to={withAdvertiseEventFormAnchor(href)} className="inline-block">
      {buttonEl}
    </Link>
  );
};

const Hero = ({ slides = [] }) => {
  const orderedSlides = [...(slides || [])].sort((a, b) => {
    const aOrder = typeof a?.order === "number" ? a.order : Number.MAX_SAFE_INTEGER;
    const bOrder = typeof b?.order === "number" ? b.order : Number.MAX_SAFE_INTEGER;
    return aOrder - bOrder;
  });

  if (!orderedSlides.length) {
    return null;
  }

  return (
    <div className="relative w-full heroslider">
      <Swiper
        modules={[Navigation, Pagination, Autoplay]}
        navigation
        pagination={{ clickable: true }}
        autoplay={{ delay: 5000, disableOnInteraction: false }}
        loop={orderedSlides.length > 1}
        className="w-full"
      >
        {orderedSlides.map((slide) => {
          const desktopImage = buildImageUrl(
            slide.backgroundImage || slide.mobileBackgroundImage
          );
          const mobileImage = buildImageUrl(
            slide.mobileBackgroundImage || slide.backgroundImage
          );
          const buttonAlign = resolveHeroButtonAlign(slide);
          const hasCopy = Boolean(
            String(slide.title || "").trim() ||
              String(slide.subtitle || "").trim() ||
              String(slide.buttonText || "").trim()
          );

          return (
            <SwiperSlide key={slide._id || desktopImage || mobileImage}>
              <div className="relative min-h-[420px] 800px:min-h-[80vh] w-full overflow-hidden flex items-center">
                {desktopImage || mobileImage ? (
                  <picture>
                    {desktopImage ? (
                      <source media="(min-width: 800px)" srcSet={desktopImage} />
                    ) : null}
                    <img
                      src={mobileImage || desktopImage}
                      alt={slide.title || ""}
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                  </picture>
                ) : null}
                {slide.overlayColor && (
                  <div
                    className="absolute inset-0"
                    style={{ backgroundColor: slide.overlayColor, opacity: 0.4 }}
                  />
                )}
                {hasCopy ? (
                  <div
                    className={`${styles.section} w-[90%] 800px:w-[80%] text-left relative z-10`}
                  >
                    {slide.title ? (
                      <h1 className="text-[34px] 800px:text-[60px] font-[600] capitalize text-[#fff] leading-[1.2]">
                        {slide.title}
                      </h1>
                    ) : null}
                    {slide.subtitle ? (
                      <p className="pt-5 font-[500] font-Poppins text-[16px] text-[#fff] max-w-[600px]">
                        {slide.subtitle}
                      </p>
                    ) : null}
                    {String(slide.buttonText || "").trim() ? (
                      <div className={`flex ${buttonJustifyClass(buttonAlign)}`}>
                        <HeroSlideButton slide={slide} className="mt-5" />
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </div>
  );
};

export default Hero;
